// src/components/AIAgent/ServiceCatalog.jsx

import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaSave,
  FaCogs,
  FaDollarSign,
  FaTag,
  FaClock,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { useAIAgentStore } from "../../services/useAIAgentStore";

const ServiceCatalog = () => {
  const {
    services,
    loading,
    fetchServices,
    createService,
    updateService,
    deleteService,
  } = useAIAgentStore();

  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    short_description: "",
    price: "",
    price_type: "fixo",
    duration_estimate: "",
    sla_response: "",
    regions_served: [],
    steps: [],
    prerequisites: "",
    category: "",
    keywords: [],
    faq: [],
  });
  const [newKeyword, setNewKeyword] = useState("");
  const [newRegion, setNewRegion] = useState("");
  const [newStep, setNewStep] = useState({ name: "", description: "" });
  const [newFaq, setNewFaq] = useState({ question: "", answer: "" });

  useEffect(() => {
    fetchServices();
  }, []);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      short_description: "",
      price: "",
      price_type: "fixo",
      duration_estimate: "",
      sla_response: "",
      regions_served: [],
      steps: [],
      prerequisites: "",
      category: "",
      keywords: [],
      faq: [],
    });
    setNewKeyword("");
    setNewRegion("");
    setNewStep({ name: "", description: "" });
    setNewFaq({ question: "", answer: "" });
    setEditingService(null);
  };

  const handleOpenForm = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name || "",
        description: service.description || "",
        short_description: service.short_description || "",
        price: service.price || "",
        price_type: service.price_type || "fixo",
        duration_estimate: service.duration_estimate || "",
        sla_response: service.sla_response || "",
        regions_served: service.regions_served || [],
        steps: service.steps || [],
        prerequisites: service.prerequisites || "",
        category: service.category || "",
        keywords: service.keywords || [],
        faq: service.faq || [],
      });
    } else {
      resetForm();
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleAddKeyword = () => {
    if (newKeyword.trim() && !formData.keywords.includes(newKeyword.trim())) {
      setFormData({
        ...formData,
        keywords: [...formData.keywords, newKeyword.trim()],
      });
      setNewKeyword("");
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setFormData({
      ...formData,
      keywords: formData.keywords.filter((k) => k !== keyword),
    });
  };

  const handleAddRegion = () => {
    if (newRegion.trim() && !formData.regions_served.includes(newRegion.trim())) {
      setFormData({
        ...formData,
        regions_served: [...formData.regions_served, newRegion.trim()],
      });
      setNewRegion("");
    }
  };

  const handleRemoveRegion = (region) => {
    setFormData({
      ...formData,
      regions_served: formData.regions_served.filter((r) => r !== region),
    });
  };

  const handleAddStep = () => {
    if (newStep.name.trim()) {
      const order = formData.steps.length + 1;
      setFormData({
        ...formData,
        steps: [...formData.steps, { order, ...newStep }],
      });
      setNewStep({ name: "", description: "" });
    }
  };

  const handleRemoveStep = (index) => {
    const newSteps = formData.steps
      .filter((_, i) => i !== index)
      .map((step, i) => ({ ...step, order: i + 1 }));
    setFormData({ ...formData, steps: newSteps });
  };

  const handleAddFaq = () => {
    if (newFaq.question.trim() && newFaq.answer.trim()) {
      setFormData({
        ...formData,
        faq: [...formData.faq, { ...newFaq }],
      });
      setNewFaq({ question: "", answer: "" });
    }
  };

  const handleRemoveFaq = (index) => {
    setFormData({
      ...formData,
      faq: formData.faq.filter((_, i) => i !== index),
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.warning("Nome do servico obrigatorio");
      return;
    }

    try {
      const data = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null,
      };

      if (editingService) {
        await updateService(editingService.id, data);
        toast.success("Servico atualizado com sucesso!");
      } else {
        await createService(data);
        toast.success("Servico criado com sucesso!");
      }
      handleCloseForm();
    } catch (err) {
      // Error handled by store
    }
  };

  const handleDelete = async (serviceId) => {
    if (window.confirm("Tem certeza que deseja excluir este servico?")) {
      try {
        await deleteService(serviceId);
        toast.success("Servico excluido com sucesso!");
      } catch (err) {
        // Error handled by store
      }
    }
  };

  const formatPrice = (price, priceType) => {
    if (priceType === "sob_consulta") return "Sob consulta";
    if (!price) return "Preco nao definido";
    const formatted = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
    const suffixes = { hora: "/hora", projeto: "/projeto", fixo: "" };
    return `${formatted}${suffixes[priceType] || ""}`;
  };

  return (
    <div className="catalog-section">
      <div className="catalog-header">
        <div>
          <h3>
            <FaCogs /> Catalogo de Servicos
          </h3>
          <p>Gerencie os servicos que o agente pode apresentar</p>
        </div>
        <button className="add-button" onClick={() => handleOpenForm()}>
          <FaPlus /> Novo Servico
        </button>
      </div>

      {services.length === 0 ? (
        <div className="empty-catalog">
          <FaCogs size={48} />
          <p>Nenhum servico cadastrado</p>
          <span>Adicione servicos para o agente poder apresenta-los aos clientes</span>
        </div>
      ) : (
        <div className="catalog-grid">
          {services.map((service) => (
            <div key={service.id} className="catalog-card">
              <div className="card-header">
                <h4>{service.name}</h4>
                <div className="card-actions">
                  <button onClick={() => handleOpenForm(service)} title="Editar">
                    <FaEdit />
                  </button>
                  <button onClick={() => handleDelete(service.id)} title="Excluir">
                    <FaTrash />
                  </button>
                </div>
              </div>

              {service.short_description && (
                <p className="card-description">{service.short_description}</p>
              )}

              <div className="card-meta">
                <span className="price">
                  <FaDollarSign />
                  {formatPrice(service.price, service.price_type)}
                </span>
                {service.category && (
                  <span className="category">
                    <FaTag />
                    {service.category}
                  </span>
                )}
              </div>

              <div className="card-footer">
                {service.duration_estimate && (
                  <span className="duration">
                    <FaClock /> {service.duration_estimate}
                  </span>
                )}
                {service.regions_served && service.regions_served.length > 0 && (
                  <span className="regions">
                    <FaMapMarkerAlt /> {service.regions_served.length} regioes
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h3>{editingService ? "Editar Servico" : "Novo Servico"}</h3>
              <button className="close-button" onClick={handleCloseForm}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-field full-width">
                  <label>Nome do Servico *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Consultoria Empresarial"
                  />
                </div>

                <div className="form-field full-width">
                  <label>Descricao Curta</label>
                  <input
                    type="text"
                    value={formData.short_description}
                    onChange={(e) =>
                      setFormData({ ...formData, short_description: e.target.value })
                    }
                    placeholder="Resumo em uma linha"
                    maxLength={500}
                  />
                </div>

                <div className="form-field full-width">
                  <label>Descricao Completa</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Descreva o servico em detalhes..."
                    rows={4}
                  />
                </div>

                <div className="form-field">
                  <label>Preco</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="form-field">
                  <label>Tipo de Preco</label>
                  <select
                    value={formData.price_type}
                    onChange={(e) =>
                      setFormData({ ...formData, price_type: e.target.value })
                    }
                  >
                    <option value="fixo">Preco Fixo</option>
                    <option value="hora">Por Hora</option>
                    <option value="projeto">Por Projeto</option>
                    <option value="sob_consulta">Sob Consulta</option>
                  </select>
                </div>

                <div className="form-field">
                  <label>Categoria</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    placeholder="Ex: Consultoria, Suporte"
                  />
                </div>

                <div className="form-field">
                  <label>Prazo Estimado</label>
                  <input
                    type="text"
                    value={formData.duration_estimate}
                    onChange={(e) =>
                      setFormData({ ...formData, duration_estimate: e.target.value })
                    }
                    placeholder="Ex: 2-4 semanas"
                  />
                </div>

                <div className="form-field">
                  <label>SLA de Resposta</label>
                  <input
                    type="text"
                    value={formData.sla_response}
                    onChange={(e) =>
                      setFormData({ ...formData, sla_response: e.target.value })
                    }
                    placeholder="Ex: 24 horas"
                  />
                </div>

                <div className="form-field full-width">
                  <label>Pre-requisitos</label>
                  <textarea
                    value={formData.prerequisites}
                    onChange={(e) =>
                      setFormData({ ...formData, prerequisites: e.target.value })
                    }
                    placeholder="O que o cliente precisa ter/fazer antes..."
                    rows={2}
                  />
                </div>

                <div className="form-field full-width">
                  <label>Regioes Atendidas</label>
                  <div className="tag-input-container">
                    <input
                      type="text"
                      value={newRegion}
                      onChange={(e) => setNewRegion(e.target.value)}
                      placeholder="Ex: Sao Paulo"
                      onKeyPress={(e) => e.key === "Enter" && handleAddRegion()}
                    />
                    <button type="button" className="buttonAdicionar"  onClick={handleAddRegion}>
                      Adicionar
                    </button>
                  </div>
                  <div className="tags-list">
                    {formData.regions_served.map((region) => (
                      <span key={region} className="tag">
                        {region}
                        <button onClick={() => handleRemoveRegion(region)}>&times;</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="form-field full-width">
                  <label>Etapas do Servico</label>
                  <div className="steps-input">
                    <input
                      type="text"
                      value={newStep.name}
                      onChange={(e) => setNewStep({ ...newStep, name: e.target.value })}
                      placeholder="Nome da etapa"
                    />
                    <input
                      type="text"
                      value={newStep.description}
                      onChange={(e) =>
                        setNewStep({ ...newStep, description: e.target.value })
                      }
                      placeholder="Descricao (opcional)"
                    />
                    <button type="button" className="buttonAdicionar"  onClick={handleAddStep}>
                      <FaPlus />
                    </button>
                  </div>
                  <div className="steps-list">
                    {formData.steps.map((step, index) => (
                      <div key={index} className="step-item">
                        <span className="step-order">{step.order}</span>
                        <div className="step-content">
                          <strong>{step.name}</strong>
                          {step.description && <p>{step.description}</p>}
                        </div>
                        <button onClick={() => handleRemoveStep(index)}>
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-field full-width">
                  <label>Palavras-chave</label>
                  <div className="tag-input-container">
                    <input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      placeholder="Digite uma palavra-chave"
                      onKeyPress={(e) => e.key === "Enter" && handleAddKeyword()}
                    />
                    <button type="button" className="buttonAdicionar" onClick={handleAddKeyword}>
                      Adicionar
                    </button>
                  </div>
                  <div className="tags-list">
                    {formData.keywords.map((keyword) => (
                      <span key={keyword} className="tag">
                        {keyword}
                        <button onClick={() => handleRemoveKeyword(keyword)}>&times;</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="form-field full-width">
                  <label>FAQ do Servico</label>
                  <div className="faq-input">
                    <input
                      type="text"
                      value={newFaq.question}
                      onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                      placeholder="Pergunta"
                    />
                    <input
                      type="text"
                      value={newFaq.answer}
                      onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                      placeholder="Resposta"
                    />
                    <button type="button" className="buttonAdicionar"  onClick={handleAddFaq}>
                      <FaPlus />
                    </button>
                  </div>
                  <div className="faq-list">
                    {formData.faq.map((item, index) => (
                      <div key={index} className="faq-item">
                        <strong>P: {item.question}</strong>
                        <p>R: {item.answer}</p>
                        <button onClick={() => handleRemoveFaq(index)}>
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="cancel-button" onClick={handleCloseForm}>
                Cancelar
              </button>
              <button className="save-button" onClick={handleSave} disabled={loading}>
                <FaSave /> {loading ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceCatalog;
